import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { HELPSCOUT_BEACON_PAGE } from "common/constants";
import {
  useGetHelpscoutBeaconsQuery,
  useUpdateHelpscoutBeaconsMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { FlexColumn, FlexRow } from "../../../Common/__styles__/Layout";
import { SettingsForm } from "../../../Common/__styles__/Settings";
import { Button } from "../../../Common/Buttons/Button";
import { SettingsPanel, SettingsSection } from "../../../Common/Settings";
import { Select, Text } from "../../../Inputs/react-hook-form";
import { HelperText } from "../../__styles__/Content";

type BeaconFormValue = {
  page: Maybe<HELPSCOUT_BEACON_PAGE>;
  beaconId: string;
};

type BeaconFormValues = {
  beacons: Array<BeaconFormValue>;
};

const PAGE_OPTIONS = [
  { value: HELPSCOUT_BEACON_PAGE.HOME, label: "Home" },
  { value: HELPSCOUT_BEACON_PAGE.EXPLORE_MAP, label: "Explore map" },
  { value: HELPSCOUT_BEACON_PAGE.PROPERTY_PROFILE, label: "Property profile" },
  { value: HELPSCOUT_BEACON_PAGE.NOT_FOUND, label: "Not found" },
  { value: HELPSCOUT_BEACON_PAGE.EC, label: "Elevation Certificates" },
  { value: HELPSCOUT_BEACON_PAGE.GET_HELP, label: "Get Help" },
];

const HelpscoutSettings = ({
  beacons,
  refetch,
}: {
  refetch: (callback: (data: Array<BeaconFormValue>) => void) => void;
  beacons: Array<BeaconFormValue>;
}) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();

  const formMethods = useForm<BeaconFormValues>({
    defaultValues: { beacons },
  });

  const {
    control,
    handleSubmit,
    register,
    watch,
    trigger,
    formState: { errors, isDirty },
    reset,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "beacons",
  });

  const [updateHelpscoutBeacons, { loading: isSaving }] =
    useUpdateHelpscoutBeaconsMutation();

  const onSubmit = handleSubmit(({ beacons }) => {
    void updateHelpscoutBeacons({
      variables: {
        data: beacons.map(({ page, beaconId }) => ({
          page: page!,
          beaconId,
        })),
      },
      onCompleted: async () => {
        addSuccessToast("Helpscout beacons saved");
        refetch(beacons => reset({ beacons }));
      },
      onError: () => {
        addErrorToast("Failed to save Helpscout beacons");
      },
    });
  });

  const addBeacon = () => {
    append({ page: null, beaconId: "" });
  };

  return (
    <SettingsPanel title="Helpscout beacons">
      <SettingsForm onSubmit={onSubmit} style={{ width: "100%" }}>
        <SettingsSection
          title="Beacon IDs"
          subtitle="Assign Helpscout beacons to the public portal pages that support them."
          actionButton={
            <Button
              title="Add beacon"
              size="small"
              onClick={addBeacon}
              isDisabled={isSaving}
              data-testid="add-beacon"
            />
          }
          fullWidth
        >
          {fields.length === 0 && (
            <HelperText>
              No Helpscout beacons configured yet. Add a beacon to start.
            </HelperText>
          )}

          <FlexColumn gap="m" style={{ width: "100%" }}>
            {fields.map((field, index) => {
              return (
                <FlexRow
                  key={field.id}
                  gap="m"
                  style={{
                    width: "100%",
                    alignItems: "flex-end",
                  }}
                  data-testid={`beacon-row-${index}`}
                >
                  <Select
                    control={control}
                    name={`beacons.${index}.page`}
                    label="Page"
                    placeholder="Select a page"
                    options={PAGE_OPTIONS}
                    size="medium"
                    disabled={isSaving}
                    required
                    rules={{
                      required: "Page is required",
                      validate: value => {
                        const updatedBeacons = watch("beacons");
                        if (
                          updatedBeacons.some(
                            (field, otherFieldIndex) =>
                              field.page === value && otherFieldIndex !== index,
                          )
                        ) {
                          return "Page must be unique for each beacon";
                        }

                        return true;
                      },
                    }}
                    error={errors.beacons?.[index]?.page?.message}
                    onChange={() => {
                      const updatedBeacons = watch("beacons");
                      updatedBeacons.forEach((field, index) => {
                        if (field.page) {
                          void trigger(`beacons.${index}.page`);
                        }
                      });
                    }}
                  />

                  <Text
                    label="Beacon ID"
                    size="medium"
                    required
                    disabled={isSaving}
                    error={errors.beacons?.[index]?.beaconId?.message}
                    {...register(`beacons.${index}.beaconId`, {
                      required: "Beacon ID is required",
                    })}
                  />

                  <Button
                    title="Remove"
                    size="small"
                    variant="critical"
                    onClick={() => {
                      remove(index);
                      const updatedBeacons = watch("beacons");
                      updatedBeacons.forEach((field, index) => {
                        if (field.page) {
                          void trigger(`beacons.${index}.page`);
                        }
                      });
                    }}
                    isDisabled={isSaving}
                    data-testid={`remove-beacon-${index}`}
                  >
                    Remove
                  </Button>
                </FlexRow>
              );
            })}
          </FlexColumn>
        </SettingsSection>

        <Button
          title="Save"
          size="medium"
          type="submit"
          isDisabled={isSaving || !isDirty}
          data-testid="save-beacons"
        />
      </SettingsForm>
    </SettingsPanel>
  );
};

const HelpscoutSettingsWrapper = () => {
  const { data, loading, error, refetch } = useGetHelpscoutBeaconsQuery();

  if (loading) {
    return <div>loading...</div>;
  }

  if (error || !data?.account) {
    return <div />;
  }

  return (
    <HelpscoutSettings
      beacons={data.account.helpscoutBeacons}
      refetch={async cb => {
        const data = await refetch();
        cb(data.data?.account?.helpscoutBeacons ?? []);
      }}
    />
  );
};

export default HelpscoutSettingsWrapper;
